import { IActivityEntity } from '../activity';
import { ClientState } from './client-state';
import { Gender } from './gender';
import { ILeadTypeEntity } from '../lead/lead-type.entity';
import { MaintenancePeriodUnit, MaintenanceSchedule } from '../maintenance/maintenance-config.entity';

export interface IClientEntity {
    client_id?: number | null;
    exact_client_number: string | null;
    exact_last_sync: Date | null;
    state: ClientState;
    inactive: boolean;
    client_data: IClientDataEntity;
    client_settings: IClientSettingsEntity;
    lead_data?: ILeadDataEntity | null;
    // Where the lead came from (FK lead_type). The companion `lead_type` is read-only, hydrated on reads.
    lead_type_id?: number | null;
    lead_type?: ILeadTypeEntity | null;
    creation_data?: Date | null;

    activities?: IActivityEntity[];
    sortableclientnumber?: number | null;
}

export interface IClientSettingsEntity {
    mailSettings: {
        disableSendMailForUpcomingMaintenance?: boolean | null;
        disableSendMailForPlannedNotification?: boolean | null;
        disableSendMailNotifyPlanningItemForHisAppointment?: boolean | null;
        disableFeedbackMail?: boolean | null;
    };
    maintenanceSettings: {
        active: boolean;
        inactive_date?: Date | null;
        // Free-form planning note for this client's maintenance contract (e.g. "only Wednesday
        // afternoons") — copied onto every auto-created maintenance as its description.
        planningNote?: string | null;
        // Contract value in euro per year — purely informational, no logic attached.
        yearlyContractAmount?: number | null;
        autoCreation?: {
            enabled: boolean;
            // Deprecated pair, still written for interval schedules; `schedule` wins when set.
            periodAmount?: number | null;
            periodUnit?: MaintenancePeriodUnit | null;
            schedule?: MaintenanceSchedule | null;
            lookaheadMonths?: number;
            // See IMaintenanceConfigEntity.allow_multiple_open. Defaults to true here (unlike
            // the global config): overrides predate the flag and expect lookahead fill.
            allowMultipleOpen?: boolean;
            createOnNewClient?: boolean;
        } | null;
        // Per-address maintenance units, keyed by IAddress.id or MAIN_ADDRESS_ID. Absent map =
        // today's behavior: one unit on the main address. The main address is enabled unless its
        // entry says enabled:false; delivery addresses are opt-in. lookahead/allowMultipleOpen/
        // createOnNewClient are deliberately not per-address — they inherit from the resolved
        // client config (or defaults when a per-address schedule stands alone).
        addresses?: {
            [addressId: string]: {
                enabled: boolean;
                // null/absent = inherit the client-resolved schedule (autoCreation override or
                // the assigned global maintenance config).
                schedule?: MaintenanceSchedule | null;
                // absent = inherit maintenanceSettings.planningNote.
                planningNote?: string | null;
            };
        } | null;
    };
}

// Sentinel address id for the client's main address. Delivery addresses carry a real uuid in
// IAddress.id; the main address never stores one — consumers reference it with this constant.
// Value export (not a bare type/const type) so the generated api client can re-export it.
export const MAIN_ADDRESS_ID = 'main';

export interface IAddress {
    // Stable identity for delivery addresses: referenced by planning items
    // (data.delivery_address_id) and per-address maintenance settings. Assigned server-side on
    // every client write; legacy entries are backfilled by migration. Never set on
    // main_address/invoice_address.
    id?: string;
    address: string | null;
    city: string | null;
    zip_code: string | null;
    country?: string | null;
    countryName?: string | null;
    state?: string | null;
    stateName?: string | null;
    location?: {
        lat: number | string | null;
        lng: number | string | null;
    };
    deleted?: boolean | null;
}

export interface IClientDataEntity {
    maintenance_inactive_date?: string;

    first_name: string | null;
    last_name: string | null;
    email: string | null;
    mobile_phone?: string | null;
    telephone_phone?: string | null;
    language?: string | null;
    gender?: Gender | null;
    remarks?: string | null;

    main_address: IAddress;
    invoice_address?: IAddress;
    delivery_addresses?: IAddress[] | null;

    exact_client_id?: string | null;

    // Set when Exact rejected this specific client (validation error); the push sweep skips
    // paused clients until an edit sets it back to false.
    exact_sync_paused?: boolean;

    is_supplier?: boolean | null;

    company_data: {
        company_name?: string | null;
        vat_number?: string | null;
        bank_account?: string | null;
        industry?: string | null;
        employees?: string | null;
    };
}

export interface IClientLeadEntity extends IClientEntity {
    state: ClientState.lead;
}

export interface ILeadDataEntity {
    lead_category_id?: number | null;
    lead_category_name?: string | null;
    lead_viewed?: Date | null;
    secret_solvari?: string | null;
    competitors?: number | string | null;
    spoken_by_solvari?: boolean | string | null;
    message_by_solvari?: string | null;
    leadViaCustomer?: number | null;
    lead_doubles?: ILeadDoubles;
    created_at: {
        date: Date;
        timezone_type?: number | null;
        timezone?: string | null;
    };
    additional_data?:
        | {
              question: string;
              answer: string;
          }[]
        | {
              [question: string]: string;
          };
    products?: {
        id: number | string;
        title: string;
    }[];
    secret?: string | null;
    title?: string | null;
    detail?: string | null;
    extra?: string | null;
    type?: string | null;
    function?: string | null;

    description: string | null;
    lead_id?: number | null;

    email?: string;
    first_name?: string;
    last_name?: string;
    address?: string;
    city?: string;
    zip_code?: string;
    mobile_phone?: string;
    lead_custom_id?: number | string | null;
    country?: string;
    employees?: string;
    industry?: string;
    telephone_phone?: string;
    vat_number?: string;
    company_name?: string;
    language?: string;
    location?: {
        lat: number;
        lng: number;
    };
    gender?: Gender;
    bank_account?: string;
    marketing_campaign?: string | null;
}

export interface ILeadDoubles {
    name_double: boolean;
    address_double: boolean;
    lead_list: {
        client_id: number;
        planning_item_type_number?: number | null;
    }[];
}
